// VideoTrimWithTimeline.tsx
import { useEffect, useRef, useState } from "react";
import { Rnd } from "react-rnd";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { Button } from "components/ui/button";

import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
import { Service } from "utils/services";
import { getFFmpegInstance } from "utils/ffmpegInstance";
import { AppLog } from "services";
import { StatusMsg } from "utils";

interface Props {
  file: File;
}

export default function VideoTrimWithTimeline({ file }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [duration, setDuration] = useState(0);
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [trimmedUrl, setTrimmedUrl] = useState<string | null>(null);
  const [trimmedThumbnails, setTrimmedThumbnails] = useState<string[]>([]);
  const [commbineUrl, setCommbineUrl] = useState<string | null>(null);

  const timelineWidth = 600;
  const thumbCount = 30;
  const maxTrimDuration = 180; // seconds

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleTrimVideo = async () => {
    if (!videoUrl) return;
    setLoading(true);

    try {
      const ffmpeg: FFmpeg = await getFFmpegInstance();

      const inputName = "input.mp4";
      const outputName = "output.mp4";

      const start = trimStart.toFixed(2);
      const durationTrim = (trimEnd - trimStart).toFixed(2);

      await ffmpeg.writeFile(inputName, await fetchFile(videoUrl));

      await ffmpeg.exec(["-ss", start, "-i", inputName, "-t", durationTrim, "-c:v", "copy", "-c:a", "copy", outputName]);

      const data = await ffmpeg.readFile(outputName);
      if (typeof data === "string") throw new Error("Expected Uint8Array from ffmpeg.readFile but received string.");
      const trimmedBlob = new Blob([new Uint8Array(data)], { type: "video/mp4" });
      const objectUrl = URL.createObjectURL(trimmedBlob);

      setTrimmedUrl(objectUrl);

      try {
        await ffmpeg.deleteFile(inputName);
      } catch (err) {
        console.warn("[ffmpeg] failed to delete input file", err);
      }

      try {
        await ffmpeg.deleteFile(outputName);
      } catch (err) {
        console.warn("[ffmpeg] failed to delete output file", err);
      }
    } catch (err) {
      console.error("[error] Trimming failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleGetThumbnails = async () => {
    if (!trimmedUrl) return;
    setLoading(true);

    try {
      const blob = await fetch(trimmedUrl).then((res) => res.blob());
      const file = new File([blob], "trimmed.mp4", { type: "video/mp4" });
      const thumbs = await Service.getThumbnails(file, 5);
      if (thumbs) {
        setTrimmedThumbnails(thumbs);
      }
    } catch (err) {
      console.error("[error] Thumbnail generation failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCombineVideo = async () => {
    if (!trimmedUrl) return;
    setLoading(true);

    try {
      const url =
        "https://flipflop-storage-001.maxx4business.com/disks/storage-001-001/282187753209901/upload_photo/2025-04-01/YgX72GnYSWHcnMoH5o8V72g8QXIbLV8nk4RLW1pj5hMTgFNzvI.m4a";
      const combine = await Service.combineVideoWithAudioUrl(file, url);

      setCommbineUrl(combine);
    } catch (err) {
      console.error("[error] Thumbnail generation failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const generateThumbnails = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    if (video.readyState < 1) {
      await new Promise((res) => {
        video.onloadedmetadata = res;
      });
    }

    const context = canvas.getContext("2d");
    const newThumbs: string[] = [];

    for (let i = 0; i < thumbCount; i++) {
      const time = (video.duration / thumbCount) * i;
      video.currentTime = time;

      await new Promise((res) => {
        video.onseeked = () => {
          context?.drawImage(video, 0, 0, canvas.width, canvas.height);
          newThumbs.push(canvas.toDataURL("image/jpeg"));
          res(true);
        };
      });
    }

    video.currentTime = 0;
    setThumbnails(newThumbs);
  };

  const handleLoadedMetadata = () => {
    const dur = videoRef.current?.duration || 0;
    setDuration(dur);
    setTrimStart(0);
    setTrimEnd(dur);
    generateThumbnails();
  };

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60)
      .toString()
      .padStart(2, "0");
    const s = Math.floor(sec % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleTrimDrag = (x: number, width: number) => {
    const start = (x / timelineWidth) * duration;
    const end = ((x + width) / timelineWidth) * duration;
    setTrimStart(start);
    setTrimEnd(end);
  };

  const togglePlay = async () => {
    try {
      if (!videoRef.current) return;
      if (playing) {
        videoRef.current.pause();
      } else {
        await videoRef.current.play();
      }
      setPlaying(!playing);
    } catch (error) {
      AppLog.show({
        message: `[togglePlay]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const update = () => {
      if (!isDragging) setCurrentTime(video.currentTime);
    };
    video.addEventListener("timeupdate", update);
    return () => video.removeEventListener("timeupdate", update);
  }, [isDragging]);

  const sliderPercentage = (currentTime / duration) * 100;

  return (
    <div className="space-y-4 relative" ref={containerRef}>
      <h2 className="text-lg font-bold">Trim Your Video</h2>

      <div className="relative w-[600px] max-w-3xl bg-black rounded-lg">
        <video
          ref={videoRef}
          src={videoUrl}
          onLoadedMetadata={handleLoadedMetadata}
          className="relative z-10 rounded w-full max-h-[500px]"
          muted={muted}
          playsInline
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
        />

        <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center pointer-events-none z-20">
          <div className="flex items-end space-x-3">
            <Button variant="secondary" size="icon" className="pointer-events-auto w-8 h-8 rounded-full" onClick={togglePlay}>
              {playing ? <Pause size={16} /> : <Play size={16} />}
            </Button>

            <Button variant="secondary" size="icon" className="pointer-events-auto w-8 h-8 rounded-full" onClick={toggleMute}>
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </Button>
          </div>
        </div>
      </div>

      <canvas ref={canvasRef} width={160} height={90} className="hidden"></canvas>

      <div className="relative w-[600px]">
        <div className="mb-4 relative">
          <div
            className="absolute z-20 top-1 transform -translate-x-1/2 text-xs text-white bg-red-600 rounded px-2 py-0.5 shadow select-none pointer-events-none"
            style={{ left: `${sliderPercentage}%` }}
          >
            {formatTime(currentTime)}
          </div>

          <div className="relative w-full h-6">
            <div className="absolute top-1/2 transform -translate-y-1/2 left-0 right-0 flex justify-between items-center px-1">
              {Array.from({ length: 17 }).map((_, i) => (
                <div key={i} className="flex flex-col items-center w-[1px] justify-center">
                  {i === 0 ? (
                    <span className="text-xs mt-1">0.00</span>
                  ) : i === 16 ? (
                    <span className="text-xs mt-1">{formatTime(duration)}</span>
                  ) : (
                    <div className="w-px h-2 mt-1 bg-current" />
                  )}
                </div>
              ))}
            </div>

            <input
              type="range"
              min={0}
              max={duration}
              step={0.01}
              value={currentTime}
              onChange={handleSeek}
              className="absolute top-0 left-0 w-full h-full bg-transparent appearance-none"
              style={{ zIndex: 10 }}
            />
          </div>
        </div>

        <div className="relative w-[600px] mt-1 overflow-hidden border rounded border-gray-300">
          <div className="flex select-none pointer-events-none">
            {thumbnails.map((src, idx) => (
              <img key={idx} src={src} alt={`thumb-${idx}`} className="w-[20px] h-[60px] object-cover" />
            ))}
          </div>

          <Rnd
            default={{ x: 0, y: 0, width: 200, height: 60 }}
            bounds="parent"
            enableResizing={{
              left: true,
              right: true,
              top: false,
              bottom: false,
            }}
            dragAxis="x"
            minWidth={(1 / duration) * timelineWidth}
            maxWidth={(maxTrimDuration / duration) * timelineWidth}
            onDragStart={() => {
              console.log("onDragStart");
              setIsDragging(true);
            }}
            onResize={() => {
              console.log("onResize");
            }}
            onResizeStart={() => {
              console.log("onResize");
            }}
            onDragStop={(_, d) => {
              handleTrimDrag(d.x, d.node.offsetWidth);
              setIsDragging(false);
            }}
            onResizeStop={(_, __, ref, ___, position) => {
              handleTrimDrag(position.x, parseFloat(ref.style.width));
              setIsDragging(false);
            }}
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              height: "60px",
              borderRadius: "6px",
              border: "2px solid #facc15",
              backgroundColor: "rgba(250, 204, 21, 0.1)",
            }}
          />
        </div>

        <div className="absolute bottom-0 w-[2px] h-[80px] bg-red-600 z-50" style={{ left: `${sliderPercentage}%` }} />
      </div>

      <p className="text-sm text-gray-600">
        Trim: {formatTime(trimStart)} → {formatTime(trimEnd)}
      </p>

      <Button onClick={handleTrimVideo} disabled={loading}>
        {loading ? "Trimming..." : "Trim Video"}
      </Button>

      <Button onClick={handleGetThumbnails} disabled={loading || !file}>
        {loading ? "Generating..." : "Get Thumbnails"}
      </Button>

      <Button onClick={handleCombineVideo} disabled={loading || !file}>
        {loading ? "Combining..." : "Combine"}
      </Button>

      {trimmedUrl && (
        <div className="mt-4">
          <h3 className="text-md font-semibold">Trimmed Video</h3>
          <video src={trimmedUrl} controls className="mt-2 max-w-full rounded border" />
          <a href={trimmedUrl} download="trimmed-video.mp4" className="inline-block mt-2 text-blue-600 hover:underline">
            Download Trimmed Video
          </a>
        </div>
      )}

      {trimmedThumbnails.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium mb-2">Trimmed Thumbnails</h4>
          <div className="flex gap-2">
            {trimmedThumbnails.map((src, i) => (
              <img key={i} src={src} alt={`trim-thumb-${i}`} className="w-[80px] h-[45px] object-cover rounded" />
            ))}
          </div>
        </div>
      )}

      {commbineUrl && (
        <div className="mt-4">
          <h3 className="text-md font-semibold">Trimmed Video</h3>
          <video src={commbineUrl} controls className="mt-2 max-w-full rounded border" />
          <a href={commbineUrl} download="trimmed-video.mp4" className="inline-block mt-2 text-blue-600 hover:underline">
            Download Trimmed Video
          </a>
        </div>
      )}
    </div>
  );
}

